'use client';

import { IFormInput, IFormProps, IFormSubmit } from '@template/components/Form';
import { useEffect, useState } from 'react';
import { Form } from '@template/components';
import useLink from '@/lib/helpers/useLink.ts';
import { useTypedSelector } from '@/redux/helpers/hooks.ts';

export type DomainNameSearchBulkProps = {
  input?: Omit<IFormInput, 'name'>;
  fileInput?: Omit<IFormInput, 'name'>;
  submit?: Omit<IFormSubmit, 'onSubmit'> & {
    href?: string;
  };
};

const DomainNameSearchBulk = ({
  input,
  fileInput,
  submit,
}: DomainNameSearchBulkProps) => {
  const link = useLink();

  const defaultValue =
    useTypedSelector((state) => state.domains.bulk.value) || '';

  const [formValue, setFormValue] = useState<{
    search: string;
    file: null | File[];
  }>({
    search: defaultValue,
    file: null,
  });
  const [fileError, setFileError] = useState<false | string>(false);

  const inputs: IFormProps['inputs'] = [
    {
      ...input,
      type: 'textarea',
      name: 'search',
    },
    {
      divider: true,
      text: 'Or',
    },
    {
      ...fileInput,
      type: 'file',
      name: 'file',
      error: fileError,
      inputProps: {
        accept: '.csv',
      },
    },
  ];

  const handleChange = (vals: any) => {
    if (fileError && vals.file !== formValue.file) {
      setFileError(false);
    }
    setFormValue(vals);
  };

  const onSubmit = (values: any) => {
    const { search, file } = values;

    if (file?.size) {
      try {
        const reader = new FileReader();

        reader.onload = (e) => {
          const fileContent = e.target?.result;
          if (!fileContent) {
            throw new Error('Error reading file');
          }
          const formattedFileContent = fileContent
            .toString()
            .replaceAll('\n', ',');

          link({
            href: (submit?.href || '') + `?search=${formattedFileContent}`,
          });
        };

        reader.onerror = (e) => {
          throw new Error('Error reading file');
        };

        reader.readAsText(file);
      } catch (e) {
        console.log(e);
        return;
      }
    } else {
      if (!search) {
        setFileError('Please enter a domain name');
        return;
      }
      link({ href: (submit?.href || '') + `?search=${search}` });
    }
  };

  useEffect(() => {
    if (!defaultValue) return;
    setFormValue({
      ...formValue,
      search: defaultValue.split(',').join(', '),
    });
  }, [defaultValue]);

  return (
    <>
      <Form
        value={formValue}
        onChange={handleChange}
        inputs={inputs}
        simple
        submit={{
          ...submit,
          text: submit.children,
          style: {
            width: 'fit-content',
            marginLeft: 'auto',
          },
          ignoreHref: true,
          onSubmit,
        }}
      />
    </>
  );
};

export default DomainNameSearchBulk;
